import React, { useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import MarketingShell from "@/components/MarketingShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill your name, email and message");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we'll get back to you soon");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <MarketingShell title="Contact us" subtitle="Questions about listings, billing, or your account? Send us a message.">
      {sent ? (
        <div data-testid="contact-success" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
          <CheckCircle weight="fill" size={40} className="text-emerald-500 mx-auto" />
          <h2 className="font-display text-2xl font-bold text-slate-900 mt-3">Thanks — message received</h2>
          <p className="text-slate-600 mt-1.5">We aim to respond within 1–2 business days at the email you provided.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Name</Label>
              <Input value={form.name} onChange={set("name")} placeholder="Jane Doe" className="h-11" data-testid="contact-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Email</Label>
              <Input type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className="h-11" data-testid="contact-email" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Subject</Label>
            <Input value={form.subject} onChange={set("subject")} placeholder="How can we help?" className="h-11" data-testid="contact-subject" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Message</Label>
            <Textarea value={form.message} onChange={set("message")} rows={5} placeholder="Tell us a bit more…" data-testid="contact-message" />
          </div>
          <Button type="submit" disabled={busy} data-testid="contact-submit"
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white h-11 px-6 group">
            {busy ? "Sending…" : "Send message"}
            <PaperPlaneTilt size={17} className="ml-1.5 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </form>
      )}
    </MarketingShell>
  );
}
